import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { CaretRight } from '@phosphor-icons/react';
import { Link } from 'react-router-dom';
import type { AdminThought, EvalItemDto, EvalRunSummary } from '@app/shared';
import { PageHeader } from '../../components/PageHeader';
import { AutoTextarea, Badge, Button, Card, Spinner } from '../../components/ui';
import { useToast } from '../../hooks/useToast';
import { useT } from '../../i18n';
import { api, type PromptSteps } from '../../lib/api';
import { errorText } from '../../lib/errors';
import { versionOf } from '../../lib/eval-review';
import { formatDate, personLabel } from '../../lib/format';
import { firstLine } from '../../lib/labels';

const inputClass =
  'w-full h-11 px-3 rounded-lg text-sm bg-surface-2 border-0 focus:outline-none focus:ring-2 focus:ring-ink/70';

function SectionTitle({ children }: { children: string }) {
  return <h2 className="text-xs font-semibold uppercase tracking-wider text-hint mb-2">{children}</h2>;
}

/** The check set and the past comparisons (spec 7.5). */
export function AdminEvalPage() {
  const t = useT();
  const qc = useQueryClient();
  const { toast, show } = useToast();
  const [adding, setAdding] = useState<null | 'thoughts' | 'paste'>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [title, setTitle] = useState('');
  const [transcript, setTranscript] = useState('');

  const items = useQuery<EvalItemDto[]>({ queryKey: ['admin', 'eval', 'items'], queryFn: () => api.adminEvalItems() });
  const runs = useQuery<EvalRunSummary[]>({ queryKey: ['admin', 'eval', 'runs'], queryFn: () => api.adminEvalRuns() });
  const prompts = useQuery<PromptSteps>({ queryKey: ['admin', 'prompts'], queryFn: () => api.adminListPrompts() });
  const thoughts = useQuery<AdminThought[]>({
    queryKey: ['admin', 'thoughts'],
    queryFn: () => api.adminThoughts(),
    enabled: adding === 'thoughts',
  });
  const version = versionOf(prompts.data);

  const act = async (key: string, call: () => Promise<unknown>, done?: string) => {
    setBusyId(key);
    try {
      await call();
      await qc.invalidateQueries({ queryKey: ['admin', 'eval', 'items'] });
      if (done) show(done, 'success');
      return true;
    } catch (err) {
      show(errorText(err, t), 'destructive');
      return false;
    } finally {
      setBusyId(null);
    }
  };

  const addPasted = async () => {
    if (await act('paste', () => api.adminAddEvalItem({ title: title.trim(), transcript: transcript.trim() }), t.adminEval.added)) {
      setTitle('');
      setTranscript('');
      setAdding(null);
    }
  };

  const inSet = new Set(items.data?.map((i) => i.sourceThoughtId).filter((id): id is number => id !== null));

  return (
    <div className="min-h-screen p-4 max-w-lg mx-auto pb-12 safe-top safe-bottom">
      <PageHeader title={t.adminEval.title} to="/admin" />

      <section className="mb-6">
        <SectionTitle>{t.adminEval.set}</SectionTitle>
        <p className="text-xs text-hint mb-2">{t.adminEval.setHint}</p>
        {items.isLoading && (
          <div className="flex justify-center py-3">
            <Spinner />
          </div>
        )}
        {items.error && <p className="text-sm text-hint">{errorText(items.error, t)}</p>}
        {items.data && items.data.length === 0 && <p className="text-sm text-hint mb-2">{t.adminEval.setEmpty}</p>}
        <ul className="space-y-2 mb-3">
          {items.data?.map((it) => (
            <li key={it.id}>
              <Card variant="surface" padding="sm" className="flex items-start gap-2">
                <span className="flex-1 min-w-0">
                  <span className="block text-sm font-medium break-words">{it.title}</span>
                  <span className="block text-xs text-hint line-clamp-2 break-words">{it.transcript}</span>
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  loading={busyId === `remove:${it.id}`}
                  onClick={() => act(`remove:${it.id}`, () => api.adminArchiveEvalItem(it.id))}
                >
                  {t.adminEval.remove}
                </Button>
              </Card>
            </li>
          ))}
        </ul>

        <div className="flex flex-wrap gap-2 mb-3">
          <Button
            variant={adding === 'thoughts' ? 'ink' : 'secondary'}
            size="sm"
            onClick={() => setAdding(adding === 'thoughts' ? null : 'thoughts')}
          >
            {t.adminEval.fromThoughts}
          </Button>
          <Button
            variant={adding === 'paste' ? 'ink' : 'secondary'}
            size="sm"
            onClick={() => setAdding(adding === 'paste' ? null : 'paste')}
          >
            {t.adminEval.paste}
          </Button>
        </div>

        {adding === 'thoughts' && (
          <Card variant="surface" padding="sm">
            {thoughts.isLoading && (
              <div className="flex justify-center py-3">
                <Spinner />
              </div>
            )}
            {thoughts.error && <p className="text-sm text-hint">{errorText(thoughts.error, t)}</p>}
            {thoughts.data && thoughts.data.every((th) => !th.transcript) && (
              <p className="text-sm text-hint">{t.adminEval.noThoughts}</p>
            )}
            <ul className="divide-y divide-line">
              {thoughts.data
                ?.filter((th) => th.transcript)
                .map((th) => (
                  <li key={th.id} className="flex items-start gap-2 py-2">
                    <span className="flex-1 min-w-0">
                      <span className="block text-xs text-hint">
                        {personLabel(th)} · {formatDate(th.createdAt)}
                      </span>
                      <span className="block text-sm line-clamp-2 break-words">{firstLine(th.transcript)}</span>
                    </span>
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={inSet.has(th.id)}
                      loading={busyId === `thought:${th.id}`}
                      onClick={() =>
                        act(`thought:${th.id}`, () => api.adminAddEvalItem({ thoughtId: th.id }), t.adminEval.added)
                      }
                    >
                      {t.adminEval.toSet}
                    </Button>
                  </li>
                ))}
            </ul>
          </Card>
        )}

        {adding === 'paste' && (
          <Card variant="surface" padding="md">
            <label className="block text-sm font-medium mb-1" htmlFor="eval-title">
              {t.adminEval.itemTitle}
            </label>
            <input
              id="eval-title"
              className={`${inputClass} mb-3`}
              value={title}
              maxLength={120}
              onChange={(e) => setTitle(e.target.value)}
            />
            <label className="block text-sm font-medium mb-1" htmlFor="eval-text">
              {t.adminEval.itemText}
            </label>
            <AutoTextarea
              id="eval-text"
              value={transcript}
              maxLength={20_000}
              minRows={5}
              maxRows={16}
              className="text-sm mb-3"
              onChange={(e) => setTranscript(e.target.value)}
            />
            <Button
              variant="ink"
              fullWidth
              disabled={!title.trim() || !transcript.trim()}
              loading={busyId === 'paste'}
              onClick={addPasted}
            >
              {t.adminEval.add}
            </Button>
          </Card>
        )}
      </section>

      <section>
        <div className="flex items-center justify-between mb-2">
          <SectionTitle>{t.adminEval.runs}</SectionTitle>
          <Link to="/admin/eval/new" className="text-sm font-medium text-fg">
            {t.adminEval.newRun}
          </Link>
        </div>
        {runs.data && runs.data.length === 0 && <p className="text-sm text-hint">{t.adminEval.runsEmpty}</p>}
        <ul className="space-y-2">
          {runs.data?.map((r) => {
            const a = version(r.aPromptId);
            const b = version(r.bPromptId);
            return (
              <li key={r.id}>
                <Link
                  to={`/admin/eval/runs/${r.id}`}
                  className="flex items-center gap-3 px-4 py-3 rounded-2xl bg-surface active:opacity-80"
                >
                  <span className="flex-1 min-w-0">
                    <span className="flex flex-wrap items-center gap-2 text-sm font-semibold">
                      {t.adminPrompts.steps[r.code]}
                      {a !== null && b !== null && (
                        <span className="text-xs font-normal text-hint">{t.adminEval.versions(a, b)}</span>
                      )}
                    </span>
                    <span className="block text-xs text-hint">
                      {t.adminEval.picked(r.picked, r.items)} · {formatDate(r.createdAt)}
                    </span>
                  </span>
                  <Badge tone={r.status === 'done' ? 'success' : r.status === 'failed' ? 'destructive' : 'neutral'}>
                    {t.adminEval.status[r.status]}
                  </Badge>
                  <CaretRight size={16} className="text-hint flex-shrink-0" />
                </Link>
              </li>
            );
          })}
        </ul>
      </section>

      {toast}
    </div>
  );
}
